import React, { useState, useEffect, useCallback } from 'react';
import { Star, Check, X, Trash2, RefreshCw, Eye, EyeOff, MessageSquare, Clock, Filter, Mail, Phone, UserX } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CONFIG = {
  pending:  { label: 'Beklemede',      color: 'bg-yellow-100 text-yellow-800 border-yellow-200',  dot: 'bg-yellow-400' },
  approved: { label: 'Onaylandı',      color: 'bg-green-100  text-green-800  border-green-200',   dot: 'bg-green-500'  },
  featured: { label: 'Öne Çıkan ⭐',  color: 'bg-orange-100 text-orange-800 border-orange-200',  dot: 'bg-orange-500' },
  rejected: { label: 'Reddedildi',     color: 'bg-red-100    text-red-800    border-red-200',     dot: 'bg-red-400'    },
};

const TAG_COLOR = {
  blue:   'bg-blue-50   text-blue-700',
  orange: 'bg-orange-50 text-orange-700',
  teal:   'bg-teal-50   text-teal-700',
  gray:   'bg-gray-100  text-gray-700',
};

function Stars({ rating = 5, size = 'sm' }) {
  const cls = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(s => (
        <Star key={s} className={`${cls} ${s <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />
      ))}
    </div>
  );
}

export default function UltraTestimonials() {
  const [items, setItems]       = useState([]);
  const [counts, setCounts]     = useState({ pending: 0, approved: 0, featured: 0, rejected: 0, guest: 0 });
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading]   = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [selected, setSelected] = useState(null);

  const token = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status_filter: statusFilter, page, limit: 15 });
      const res = await fetch(`${API}/ultra/testimonials?${params}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error('API hatası');
      const data = await res.json();
      setItems(data.items || []);
      setCounts(data.counts || {});
      setTotal(data.total || 0);
      setTotalPages(Math.max(1, Math.ceil((data.total || 0) / 15)));
    } catch (e) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (testimonial_id, newStatus) => {
    if (newStatus === 'featured' && counts.featured >= 3) {
      toast.error('Zaten 3 öne çıkan yorum var. Önce birini kaldırın.');
      return;
    }
    setActionLoading(testimonial_id);
    try {
      const res = await fetch(`${API}/ultra/testimonials/${testimonial_id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || 'Hata');
      }
      toast.success(`Durum "${STATUS_CONFIG[newStatus]?.label}" olarak güncellendi`);
      if (selected?.testimonial_id === testimonial_id) setSelected(null);
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Güncelleme başarısız');
    } finally {
      setActionLoading(null);
    }
  };

  const deleteTestimonial = async (testimonial_id) => {
    if (!window.confirm('Bu yorumu kalıcı olarak silmek istediğinize emin misiniz?')) return;
    setActionLoading(testimonial_id);
    try {
      const res = await fetch(`${API}/ultra/testimonials/${testimonial_id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error('Silme başarısız');
      toast.success('Yorum silindi');
      if (selected?.testimonial_id === testimonial_id) setSelected(null);
      fetchData();
    } catch {
      toast.error('Silme başarısız');
    } finally {
      setActionLoading(null);
    }
  };

  const kpiCards = [
    { label: 'Beklemede',     value: counts.pending,  icon: Clock,    color: 'text-yellow-600 bg-yellow-50', border: 'border-yellow-100' },
    { label: 'Onaylandı',     value: counts.approved, icon: Check,    color: 'text-green-600  bg-green-50',  border: 'border-green-100'  },
    { label: 'Öne Çıkan',     value: `${counts.featured}/3`, icon: Star, color: 'text-orange-600 bg-orange-50', border: 'border-orange-100' },
    { label: 'Misafir Yorum', value: counts.guest || 0, icon: UserX,  color: 'text-purple-600 bg-purple-50', border: 'border-purple-100' },
  ];

  const FILTER_TABS = [
    { key: 'all',      label: 'Tümü' },
    { key: 'pending',  label: 'Beklemede' },
    { key: 'approved', label: 'Onaylandı' },
    { key: 'featured', label: 'Öne Çıkan' },
    { key: 'rejected', label: 'Reddedildi' },
    { key: 'guest',    label: 'Misafirler', highlight: true },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Kullanıcı Yorumları</h1>
          <p className="text-sm text-gray-500 mt-0.5">Kullanıcıların gönderdiği referansları yönetin</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          data-testid="testimonials-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Yenile
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map(kpi => (
          <Card key={kpi.label} className={`border ${kpi.border} shadow-none`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${kpi.color}`}>
                <kpi.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
                <p className="text-xs text-gray-500">{kpi.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Featured limit warning */}
      {counts.featured >= 3 && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-orange-50 border border-orange-200 rounded-xl text-sm text-orange-700">
          <Star className="w-4 h-4 fill-orange-400 text-orange-400 shrink-0" />
          Maksimum 3 öne çıkan yorum ana sayfada gösterilir. Yeni yorum öne çıkarmak için mevcut bir tanesini önce "Onaylandı" durumuna alın.
        </div>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-gray-400 shrink-0" />
          {FILTER_TABS.map(({ key, label, highlight }) => (
            <button
              key={key}
              onClick={() => { setStatusFilter(key); setPage(1); }}
              data-testid={`filter-${key}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-all ${
                statusFilter === key
                  ? highlight
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-[#0B1E4D] text-white border-[#0B1E4D]'
                  : highlight
                    ? 'text-purple-600 border-purple-200 hover:border-purple-400 hover:bg-purple-50'
                    : 'text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              {label}
              {key !== 'all' && (counts[key] > 0) && (
                <span className="ml-1.5 opacity-70">{counts[key]}</span>
              )}
            </button>
          ))}
          <span className="ml-auto text-xs text-gray-400">{total} yorum</span>
        </CardContent>
      </Card>

      {/* Guest info banner */}
      {statusFilter === 'guest' && (
        <div className="flex items-start gap-3 px-4 py-3 bg-purple-50 border border-purple-200 rounded-xl">
          <UserX className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-purple-800">Misafir Yorumları</p>
            <p className="text-xs text-purple-600 mt-0.5">
              Giriş yapmadan yorum gönderen ziyaretçiler. E-posta ve (varsa) telefon bilgileri tabloda görüntülenmektedir.
            </p>
          </div>
        </div>
      )}

      {/* Table */}
      <Card className="border-0 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-[#0B1E4D] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <MessageSquare className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm font-medium">Bu filtrede yorum bulunamadı</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide px-5 py-3">Kullanıcı</th>
                  {statusFilter === 'guest' && (
                    <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide px-3 py-3">İletişim</th>
                  )}
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide px-3 py-3">Yorum</th>
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide px-3 py-3">Puan</th>
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide px-3 py-3">Durum</th>
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide px-3 py-3">Tarih</th>
                  <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wide px-5 py-3">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map(item => {
                  const sc = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
                  const tc = TAG_COLOR[item.tag_color] || TAG_COLOR.gray;
                  const isLoading = actionLoading === item.testimonial_id;
                  const isGuest = !!item.guest_email;
                  return (
                    <tr key={item.testimonial_id} className={`hover:bg-gray-50/50 transition-colors ${isGuest ? 'bg-purple-50/20' : ''}`}>
                      {/* User */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          {item.avatar
                            ? <img src={item.avatar} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                            : <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${isGuest ? 'bg-gradient-to-br from-purple-400 to-purple-600' : 'bg-gradient-to-br from-orange-400 to-amber-500'}`}>
                                {isGuest ? <UserX className="w-4 h-4" /> : (item.user_name || '?')[0]}
                              </div>}
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="font-semibold text-gray-900 text-xs truncate max-w-[120px]">{item.user_name || 'Misafir'}</p>
                              {isGuest && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 whitespace-nowrap">Misafir</span>
                              )}
                            </div>
                            <p className="text-[10px] text-gray-400 truncate max-w-[140px]">{item.user_role}</p>
                            {item.tag && !isGuest && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${tc}`}>{item.tag}</span>}
                          </div>
                        </div>
                      </td>

                      {/* Guest contact info column */}
                      {statusFilter === 'guest' && (
                        <td className="px-3 py-3.5">
                          <div className="space-y-1">
                            {item.guest_email && (
                              <div className="flex items-center gap-1.5 text-xs text-gray-700">
                                <Mail className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                                <a href={`mailto:${item.guest_email}`} className="hover:text-purple-600 hover:underline truncate max-w-[160px]">
                                  {item.guest_email}
                                </a>
                              </div>
                            )}
                            {item.guest_phone && (
                              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                <Phone className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                                <span>{item.guest_phone}</span>
                              </div>
                            )}
                            {!item.guest_phone && (
                              <p className="text-[10px] text-gray-400 pl-5">Telefon yok</p>
                            )}
                          </div>
                        </td>
                      )}

                      {/* Text preview */}
                      <td className="px-3 py-3.5 max-w-xs">
                        <p className="text-xs text-gray-600 leading-relaxed line-clamp-2 italic">"{item.text}"</p>
                      </td>

                      {/* Rating */}
                      <td className="px-3 py-3.5">
                        <Stars rating={item.rating} />
                      </td>

                      {/* Status */}
                      <td className="px-3 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold rounded-full border ${sc.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                          {sc.label}
                        </span>
                      </td>

                      {/* Date */}
                      <td className="px-3 py-3.5">
                        <p className="text-xs text-gray-400">
                          {item.created_at ? new Date(item.created_at).toLocaleDateString('tr-TR', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                        </p>
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => setSelected(item)} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Detay" data-testid={`view-btn-${item.testimonial_id}`}>
                            <Eye className="w-4 h-4" />
                          </button>
                          {(item.status === 'pending' || item.status === 'rejected') && (
                            <button onClick={() => updateStatus(item.testimonial_id, 'approved')} disabled={isLoading} className="p-1.5 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors disabled:opacity-40" title="Onayla" data-testid={`approve-btn-${item.testimonial_id}`}>
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          {item.status === 'approved' && (
                            <button onClick={() => updateStatus(item.testimonial_id, 'featured')} disabled={isLoading || counts.featured >= 3} className="p-1.5 rounded-lg text-gray-400 hover:text-orange-600 hover:bg-orange-50 transition-colors disabled:opacity-40" title={counts.featured >= 3 ? 'Maks 3 öne çıkan' : 'Öne Çıkar'} data-testid={`feature-btn-${item.testimonial_id}`}>
                              <Star className="w-4 h-4" />
                            </button>
                          )}
                          {item.status === 'featured' && (
                            <button onClick={() => updateStatus(item.testimonial_id, 'approved')} disabled={isLoading} className="p-1.5 rounded-lg text-orange-500 hover:text-gray-500 hover:bg-gray-50 transition-colors" title="Öne Çıkarmayı Kaldır" data-testid={`unfeature-btn-${item.testimonial_id}`}>
                              <EyeOff className="w-4 h-4" />
                            </button>
                          )}
                          {item.status !== 'rejected' && (
                            <button onClick={() => updateStatus(item.testimonial_id, 'rejected')} disabled={isLoading} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40" title="Reddet" data-testid={`reject-btn-${item.testimonial_id}`}>
                              <X className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => deleteTestimonial(item.testimonial_id)} disabled={isLoading} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40" title="Sil" data-testid={`delete-btn-${item.testimonial_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                          {isLoading && <div className="w-4 h-4 border-2 border-[#0B1E4D] border-t-transparent rounded-full animate-spin" />}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
            className="px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 transition-colors">
            ← Önceki
          </button>
          <span className="text-sm text-gray-500">{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
            className="px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 transition-colors">
            Sonraki →
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={e => e.target === e.currentTarget && setSelected(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-gray-900">Yorum Detayı</h3>
                {selected.guest_email && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Misafir</span>
                )}
              </div>
              <button onClick={() => setSelected(null)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* User info */}
              <div className="flex items-center gap-3">
                {selected.avatar
                  ? <img src={selected.avatar} alt="" className="w-12 h-12 rounded-full object-cover" />
                  : <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${selected.guest_email ? 'bg-gradient-to-br from-purple-400 to-purple-600' : 'bg-gradient-to-br from-orange-400 to-amber-500'}`}>
                      {selected.guest_email ? <UserX className="w-5 h-5" /> : (selected.user_name || '?')[0]}
                    </div>}
                <div>
                  <p className="font-bold text-gray-900">{selected.user_name || 'Misafir'}</p>
                  <p className="text-sm text-gray-500">{selected.user_role}</p>
                  {selected.tag && !selected.guest_email && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${TAG_COLOR[selected.tag_color] || TAG_COLOR.gray}`}>{selected.tag}</span>}
                </div>
                <div className="ml-auto">
                  <Stars rating={selected.rating} size="md" />
                </div>
              </div>

              {/* Guest contact info */}
              {selected.guest_email && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 space-y-1.5">
                  <p className="text-xs font-semibold text-purple-700 mb-1">İletişim Bilgileri</p>
                  <div className="flex items-center gap-2 text-xs text-purple-800">
                    <Mail className="w-3.5 h-3.5 text-purple-500" />
                    <a href={`mailto:${selected.guest_email}`} className="hover:underline font-medium">{selected.guest_email}</a>
                  </div>
                  {selected.guest_phone && (
                    <div className="flex items-center gap-2 text-xs text-purple-800">
                      <Phone className="w-3.5 h-3.5 text-purple-500" />
                      <span>{selected.guest_phone}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Status */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-medium">Durum:</span>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-full border ${STATUS_CONFIG[selected.status]?.color}`}>
                  {STATUS_CONFIG[selected.status]?.label}
                </span>
                <span className="text-xs text-gray-400 ml-auto">
                  {selected.created_at ? new Date(selected.created_at).toLocaleString('tr-TR') : ''}
                </span>
              </div>

              {/* Text */}
              <blockquote className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-4 italic border-l-4 border-orange-300">
                "{selected.text}"
              </blockquote>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-2 pt-2">
                {(selected.status === 'pending' || selected.status === 'rejected') && (
                  <button onClick={() => updateStatus(selected.testimonial_id, 'approved')}
                    className="flex-1 py-2.5 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-xl transition-colors">
                    Onayla
                  </button>
                )}
                {selected.status === 'approved' && (
                  <button onClick={() => updateStatus(selected.testimonial_id, 'featured')}
                    disabled={counts.featured >= 3}
                    className="flex-1 py-2.5 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-xl transition-colors disabled:opacity-50">
                    Öne Çıkar ⭐
                  </button>
                )}
                {selected.status === 'featured' && (
                  <button onClick={() => updateStatus(selected.testimonial_id, 'approved')}
                    className="flex-1 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
                    Öne Çıkarmayı Kaldır
                  </button>
                )}
                {selected.status !== 'rejected' && (
                  <button onClick={() => updateStatus(selected.testimonial_id, 'rejected')}
                    className="flex-1 py-2.5 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 rounded-xl transition-colors">
                    Reddet
                  </button>
                )}
                <button onClick={() => deleteTestimonial(selected.testimonial_id)}
                  className="px-4 py-2.5 text-sm font-semibold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-xl transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
